import type { RequestTourLandingPageInterface } from "@/libs/server/goApiServer/tours/toursServiceInterface/getTourDestinationsInterface";
import { ResponseTourLandingPage } from "@/libs/server/goApiServer/tours/toursServiceInterface/getTourDestinationsInterface";
import { apiRequest } from "@/utils/common/serviceApiHelper/serviceApiUtils";
import usePath from "@/utils/hooks/client/usePath";

import type {
  RequestFestivalToursInterface,
  RequestHolidayToursInterface,
} from "./toursServiceInterface/getSearchToursInterface";
import {
  ResponseFestivalTours,
  ResponseHolidayTours,
  ResponseHotDealsTours,
  ResponseSearchTours,
} from "./toursServiceInterface/getSearchToursInterface";
import {
  ResponseTourDestinationRecommend,
  ResponseTourSuggestion,
} from "./toursServiceInterface/getTourDestinationRecommendInterface";
import { ResponseTourRecentlyView } from "./toursServiceInterface/getTourRecentlyViewInterface";
import type { AllocateTourParams } from "./toursServiceInterface/postAllocateTour";
import { ResponseAllocateTour } from "./toursServiceInterface/postAllocateTour";
import type { RequestPreAllocateTour } from "./toursServiceInterface/postPreallocateTourInterface";
import { ResponsePreAllocateTour } from "./toursServiceInterface/postPreallocateTourInterface";
import type { PrivateTourParams } from "./toursServiceInterface/postPrivateTour";
import { ResponsePrivateTour } from "./toursServiceInterface/postPrivateTour";

class ToursServiceApi {
  private baseUrl: string;

  private basePath: string;

  private basePathV2: string;

  private basePathV5: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.basePath = "tour";
    this.basePathV2 = `v2/${this.basePath}`;
    this.basePathV5 = `v5/`;
  }

  async getSearchTours(
    params: RequestPreAllocateTour,
  ): Promise<ResponseSearchTours> {
    const queryParams = new URLSearchParams(params as any);

    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePathV2}/SearchTour?${queryParams}`,
      "GET",
    )) as any;

    return new ResponseSearchTours(response);
  }

  async postPreallocateTours(
    params: RequestPreAllocateTour,
  ): Promise<ResponsePreAllocateTour | null> {
    try {
      const response = (await apiRequest(
        this.baseUrl,
        `${this.basePathV2}/PreAllocate`,
        "POST",
        params,
      )) as any;

      return new ResponsePreAllocateTour(response);
    } catch (error) {
      return new ResponsePreAllocateTour({});
    }
  }

  async postAllocateTours(
    params: AllocateTourParams,
  ): Promise<ResponseAllocateTour | null> {
    try {
      const response = (await apiRequest(
        this.baseUrl,
        `${this.basePathV2}/allocate`,
        "POST",
        params,
      )) as any;

      return new ResponseAllocateTour(response);
    } catch (error) {
      return new ResponseAllocateTour({});
    }
  }

  async postPrivateTour(
    params: PrivateTourParams,
  ): Promise<ResponsePrivateTour | null> {
    try {
      const response = (await apiRequest(
        this.baseUrl,
        `${this.basePathV5}/PrivateTour`,
        "POST",
        params,
      )) as any;

      return new ResponsePrivateTour(response);
    } catch (error) {
      return new ResponsePrivateTour({});
    }
  }

  async getHolidayTours(
    params: RequestHolidayToursInterface,
  ): Promise<ResponseHolidayTours> {
    const queryParams = new URLSearchParams(params as any);

    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePathV2}/HolidayTour?${queryParams}`,
      "GET",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      true,
      undefined,
    )) as any;

    return new ResponseHolidayTours(response);
  }

  async getFestivalTour(
    params: RequestFestivalToursInterface,
  ): Promise<ResponseFestivalTours> {
    const queryParams = new URLSearchParams(params as any);

    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePathV2}/FestivalTour?${queryParams}`,
      "GET",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      true,
      undefined,
    )) as any;

    return new ResponseFestivalTours(response);
  }

  async getTourDestinationRecommend(): Promise<ResponseTourDestinationRecommend> {
    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePathV2}/tourDestinationRecommend`,
      "GET",
    )) as any;

    return new ResponseTourDestinationRecommend(response);
  }

  async getHotDealTour(
    params: RequestFestivalToursInterface,
  ): Promise<ResponseHotDealsTours> {
    const queryParams = new URLSearchParams(params as any);

    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePathV2}/lastMinuteAndDiscount?${queryParams}`,
      "GET",
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      true,
    )) as any;

    return new ResponseHotDealsTours(response);
  }

  async getTourSuggestion(): Promise<ResponseTourSuggestion> {
    try {
      const { dynamicImportJsonPath } = usePath();
      const responseJson: ResponseTourSuggestion = await dynamicImportJsonPath(
        `${this.basePathV2}/tourSuggestion`,
      );

      if (responseJson && responseJson.status) {
        return new ResponseTourSuggestion(responseJson);
      }

      // Fallback to API request if responseJson is invalid
      const response = (await apiRequest(
        this.baseUrl,
        `${this.basePathV2}/tourSuggestion`,
        "GET",
      )) as any;
      return new ResponseTourSuggestion(response);
    } catch (error) {
      // console.error(error);
      return new ResponseTourSuggestion({});
    }
  }

  async getTourRecentlyView(params: number[]): Promise<any> {
    try {
      const response = (await apiRequest(
        this.baseUrl,
        `${this.basePathV2}/GetRecently?tourIds=[${params}]`,
        "GET",
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        false,
      )) as any;
      return new ResponseTourRecentlyView(response);
    } catch (error) {
      return new ResponseTourRecentlyView({});
    }
  }

  async getLandingPage(
    params: RequestTourLandingPageInterface,
  ): Promise<ResponseTourLandingPage | undefined> {
    const queryParams = new URLSearchParams(params as any);

    const response = (await apiRequest(
      this.baseUrl,
      `${this.basePathV2}/getLandingPage?${queryParams}`,
      "GET",
      undefined,
      undefined,
      undefined,
      undefined,
      6000,
    )) as any;

    if (!response || !response.status || response.status === false) {
      return undefined;
    }

    return new ResponseTourLandingPage(response.data);
  }
}
export default ToursServiceApi;
